// src/services/indicatorsService.js
import { fetchWithAuth } from "../utils/fetchWithAuth";

const BASE_URL = `/indicators`;

// Campos que sí acepta la API (lado escritura)
const ALLOWED_FIELDS = [
  "label",
  "value",           // string libre
  "prefix",
  "color",
  "hint",
  "periodicity",
  "source_label",
  "source_url",
  "order_index",
  "last_updated_at",       // fecha de la fuente (input del usuario)
];

// ---------- helpers ----------
function ensureHttps(url = "") {
  const t = String(url).trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function normalizeColor(c, fallback = "#0B4A8F") {
  if (!c) return fallback;
  const s = String(c).trim();
  if (/^#([0-9a-f]{6})$/i.test(s)) return s;
  // a veces viene "0B4A8F" sin "#"
  if (/^([0-9a-f]{6})$/i.test(s)) return `#${s}`;
  return fallback;
}

// Normaliza el registro que viene del backend a las claves que usa la UI
function normalizeIndicatorRecord(rec = {}, i = 0) {
  const palette = ["#7C3AED", "#10B981", "#06B6D4", "#F59E0B", "#EF4444", "#3B82F6"];

  const color = normalizeColor(rec.color, palette[i % palette.length]);

  // ⬇⬇ IMPORTANTE: ya NO mezclamos fecha de fuente con updated_at
  const sourceDate =
    rec.last_updated_at ||
    rec.lastUpdatedAt ||
    "";

  // fecha de edición interna (UpdateDateColumn)
  const editDate =
    rec.updated_at ||
    rec.updatedAt ||
    "";

  const periodicity = rec.periodicity ?? "";

  return {
    // lo que venga crudo de la API…
    ...rec,

    // …más defaults y alias que usa la UI:
    prefix: rec.prefix ?? "",
    color,
    source_url: rec.source_url ?? rec.sourceUrl ?? "",
    source_label: rec.source_label ?? rec.sourceLabel ?? "",
    periodicity,

    // Fecha de la fuente (lo que llenas en el modal)
    last_updated_at: sourceDate,

    // Fecha de edición interna (solo lectura, para mostrar)
    updated_at: editDate,
  };
}

// Limpia el payload y fuerza tipos compatibles con el backend nuevo (lado escritura)
function sanitize(payload = {}) {
  const out = {};

  for (const [k, rawV] of Object.entries(payload)) {
    if (!ALLOWED_FIELDS.includes(k)) continue;

    const v = rawV ?? undefined;
    if (v === undefined) continue;

    // strings que no queremos guardar vacíos
    if (
      [
        "label",
        "prefix",
        "hint",
        "source_label",
        "periodicity",
      ].includes(k)
    ) {
      // Permitir null explícitamente para borrar valores
      if (v === null) {
        out[k] = null;
        continue;
      }
      const t = String(v).trim();
      if (t === "") continue;
      out[k] = t;
      continue;
    }

    if (k === "value") {
      out[k] = String(v);
      continue;
    }

    if (k === "source_url") {
      const t = ensureHttps(v);
      if (!t) continue;
      out[k] = t;
      continue;
    }

    if (k === "color") {
      out[k] = normalizeColor(v);
      continue;
    }

    if (k === "order_index") {
      const n = Number(v);
      if (Number.isFinite(n)) out[k] = n;
      continue;
    }

    if (k === "last_updated_at") {
      const t = String(v).trim();
      if (!t) continue; // si está vacío no se manda (no pisa el valor previo)
      out[k] = t;
      continue;
    }

    // fallback general (por si agregas algo más en el futuro)
    out[k] = v;
  }

  return out;
}

// Validación mínima para crear
function assertCreate(payload) {
  if (!payload?.label || !String(payload.label).trim()) {
    throw new Error("Falta 'label'");
  }
}

// ---------- API ----------
export async function listIndicators() {
  // cache-buster por si hay CDN/proxy
  const url = `${BASE_URL}?t=${Date.now()}`;
  const res = await fetchWithAuth(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }

  const json = await res.json().catch(() => []);
  const arr = Array.isArray(json)
    ? json
    : Array.isArray(json?.data)
    ? json.data
    : [];

  return (arr || []).map((rec, i) => normalizeIndicatorRecord(rec, i));
}

export async function createIndicator(payload) {
  assertCreate(payload);
  const body = sanitize(payload);

  const res = await fetchWithAuth(`${BASE_URL}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  }
  return normalizeIndicatorRecord(data);
}

export async function updateIndicator(id, payload) {
  if (id === undefined || id === null) throw new Error("Falta 'id'");

  const body = sanitize(payload);

  const res = await fetchWithAuth(`${BASE_URL}/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  }
  return normalizeIndicatorRecord(data);
}

export async function deleteIndicator(id) {
  if (id === undefined || id === null) throw new Error("Falta 'id'");

  const res = await fetchWithAuth(`${BASE_URL}/${id}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return true;
}
